import { createStyles } from '@mantine/core';
import { useLayoutEffect, useRef } from 'react';

import { HEADER_HEIGHT } from '~/domains/common/components/AppShellHeader2/constants';
import type { PropsFilterLeague } from '~/domains/contest/domains/services/interfaces/propsService/api';
import { ContainerNested } from '~/domains/design-system/Container';
import { Pill } from '~/domains/design-system/Pill';

import { QUICK_PICKS_TABS_HEIGHT } from '../../QuickPickLayout/QuickPickLayout';
import { IconMapByLeague } from '../IconMap';
import dt from '~/testing';

export const useStyles = createStyles(
  (theme, { isBelowSubNav, isDiscover }: { isBelowSubNav?: boolean; isDiscover?: boolean }) => ({
    container: {
      display: 'flex',
      overflowX: 'auto',
      gap: theme.other.spacing._8,
      position: isDiscover ? 'sticky' : 'inherit',
      top: isBelowSubNav ? HEADER_HEIGHT + QUICK_PICKS_TABS_HEIGHT : HEADER_HEIGHT,
      zIndex: isDiscover ? 89 : 'inherit',
      paddingTop: theme.other.spacing._16,
      paddingBottom: theme.other.spacing._16,
      backgroundColor: theme.colors.gray2[0],
      marginBottom: -theme.other.spacing._16,
    },
  })
);

function Item({
  option,
  isActive,
  onChange,
}: {
  option: PropsFilterLeague;
  isActive?: boolean;
  onChange: (newValue: string) => void;
}) {
  const elRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    if (!elRef.current) {
      return;
    }
    if (!isActive) {
      return;
    }

    elRef.current.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'center' });
  }, [isActive]);

  return (
    <Pill
      data-test-id={dt.contest.quickPicks.detail.general.filterButton}
      // @ts-expect-error Some legacy mantine issues
      ref={elRef}
      selected={isActive}
      disabled={!option.active}
      onClick={() => {
        if (!option.active) {
          return;
        }
        onChange(option.id);
      }}
    >
      <IconMapByLeague leagueId={option.id} size={16} />
      {option.display_name}
    </Pill>
  );
}

function FilterLeague({
  options,
  value,
  onChange,
  isBelowSubNav,
}: {
  options: PropsFilterLeague[];
  value: string;
  onChange: (newValue: string) => void;
  isBelowSubNav?: boolean;
}) {
  const { classes } = useStyles({ isBelowSubNav, isDiscover: value === 'popular' });

  return (
    <ContainerNested className={classes.container}>
      {options.map((option) => (
        <Item
          key={option.id}
          option={option}
          isActive={option.id === value}
          onChange={() => {
            if (option.id === value) {
              return;
            }

            onChange(option.id);
          }}
        />
      ))}
    </ContainerNested>
  );
}

export default FilterLeague;
